import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  ArithmeticOperator,
  ArithmeticTransform,
  ArithmeticVariableType,
} from 'lab-automation/settings/RunSchemaSpecConstants';
import DropdownValueForm from 'lookup-config/DropdownValueForm';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_DARKER} from 'ui-library/Colors';
import ErrorMessage from 'ui-library/ErrorMessage';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import NumericInput from 'ui-library/inputs/NumericInput';
import TextField from 'ui-library/inputs/TextField';
import {NumericInputChangeEvent} from 'util/Types';

import {ArithmeticFormErrors, updateTransformationProperties} from './TransformFormUtils';

interface ArithmeticVariableFormProps {
  value:
    | {
        type?: ArithmeticVariableType;
        value?: string | number;
      }
    | undefined;
  onChange: (value: {type?: ArithmeticVariableType; value?: string | number}) => void;
  formErrors: {type: boolean; value: boolean} | undefined;
}

const typeSelectorOptions: {[variableType in ArithmeticVariableType]: string} = {
  [ArithmeticVariableType.COLUMN]: 'Column',
  [ArithmeticVariableType.CONSTANT]: 'Constant',
};

const operatorSelectorOptions: {[operator in ArithmeticOperator]: string} = {
  [ArithmeticOperator.ADDITION]: 'Add',
  [ArithmeticOperator.SUBTRACTION]: 'Subtract',
  [ArithmeticOperator.MULTIPLICATION]: 'Multiply',
  [ArithmeticOperator.DIVISION]: 'Divide',
};

function ArithmeticVariableForm(props: ArithmeticVariableFormProps): JSX.Element {
  return (
    <Row className={css(styles.row)}>
      <Col md={4}>
        <DropdownValueForm
          isRequired={true}
          label="Input type"
          value={props.value?.type}
          onUpdate={(type: ArithmeticVariableType) => {
            props.onChange({type});
          }}
          options={Object.values(ArithmeticVariableType).map((type) => ({
            text: typeSelectorOptions[type],
            value: type,
          }))}
          error={props.formErrors?.type ? 'Input type is required' : null}
        />
      </Col>
      <Col md={4}>
        {props.value?.type === ArithmeticVariableType.COLUMN ? (
          <>
            <TextField
              label="Column name"
              onChange={(e) => {
                props.onChange({
                  ...props.value,
                  value: e.target.value,
                });
              }}
              labelStyle={FieldLabel.STYLES.LABEL}
              isRequired={true}
              value={props.value.value as string | undefined}
              hasError={props.formErrors?.value}
            />
            {props.formErrors?.value ? <ErrorMessage error="Column name is required" /> : null}
          </>
        ) : null}
        {props.value?.type === ArithmeticVariableType.CONSTANT ? (
          <>
            <FieldLabel label="Specify constant" isRequired={true} />
            <NumericInput
              value={props.value.value}
              onChange={(e: NumericInputChangeEvent) => {
                if (e.target.numericValue != null) {
                  props.onChange({
                    ...props.value,
                    value: e.target.numericValue,
                  });
                }
              }}
              required={props.formErrors?.value}
            />
            {props.formErrors?.value ? <ErrorMessage error="Constant value is required" /> : null}
          </>
        ) : null}
      </Col>
    </Row>
  );
}

interface ArithmeticFormProps {
  transform: ArithmeticTransform;
  onChange: (transform: ArithmeticTransform) => void;
  formErrors: ArithmeticFormErrors;
}

export default function ArithmeticForm(props: ArithmeticFormProps): JSX.Element {
  return (
    <>
      <Row className={css(styles.row)}>
        <Col md={4}>
          <DropdownValueForm
            isRequired={true}
            label="Operator"
            value={props.transform.transformationProperties?.operator}
            onUpdate={(value: ArithmeticOperator) => {
              props.onChange(updateTransformationProperties(props.transform, 'operator', value));
            }}
            options={Object.values(ArithmeticOperator).map((operator) => ({
              text: operatorSelectorOptions[operator],
              value: operator,
            }))}
            error={props.formErrors.operator ? 'Operator is required' : null}
          />
        </Col>
        <Col md={4}>
          <TextField
            label="New column name"
            onChange={(e) => {
              props.onChange(
                updateTransformationProperties(props.transform, 'newColumnName', e.target.value)
              );
            }}
            labelStyle={FieldLabel.STYLES.LABEL}
            isRequired={true}
            value={props.transform.transformationProperties?.newColumnName}
            hasError={props.formErrors.newColumnName}
          />
          {props.formErrors.newColumnName ? <ErrorMessage error="New column name is required" /> : null}
        </Col>
      </Row>
      <div className={css(styles.valueLabel)}>Value 1</div>
      <ArithmeticVariableForm
        onChange={(firstVariable) => {
          props.onChange(updateTransformationProperties(props.transform, 'firstVariable', firstVariable));
        }}
        value={props.transform.transformationProperties?.firstVariable}
        formErrors={{type: props.formErrors.firstVariableType, value: props.formErrors.firstVariableValue}}
      />
      <div className={css(styles.valueLabel)}>Value 2</div>
      <ArithmeticVariableForm
        onChange={(secondVariable) => {
          props.onChange(updateTransformationProperties(props.transform, 'secondVariable', secondVariable));
        }}
        value={props.transform.transformationProperties?.secondVariable}
        formErrors={{type: props.formErrors.secondVariableType, value: props.formErrors.secondVariableValue}}
      />
      {props.formErrors.type ? (
        <ErrorMessage error='Cannot specify two constant variables. Use an "Add Column" transform instead.' />
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  row: {
    paddingBottom: '16px',
  },
  valueLabel: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
  },
});
